export const getOrderStatusDate = order => {
  const status = order.virtual_status
  const isPickupOrder = order?.shipping_method?.shipping_type === 'pickup'

  switch (true) {
    case status === 'initial':
    case status === 'empty':
    case status === 'entirely_dropshipping':
    case status === 'cancelation_window':
    case status === 'unpaid':
    case status === 'backorder':
    case status === 'error':
      return order.order_date

    case status === 'pending' && order.pending_at:
      return order.pending_at

    case status === 'hold':
    case status === 'holded':
    case status === 'returning':
    case status === 'returned':
      return null

    case status === 'shipped' && isPickupOrder:
    case status === 'waiting_pick_up':
      return order.shipping_date

    case status === 'canceled':
      return order.updated_at

    case status === 'shipped' && !isPickupOrder:
      return order.dispatched_at

    default:
      return null
  }
}

export const formatShippingMethodFilterLabel = shippingMethod => {
  if (!shippingMethod) return '-'
  return `${shippingMethod.carrier_name} - ${shippingMethod.shipping_name}`
}

export const formatShippingMethodFilterId = shippingMethod => {
  return `${shippingMethod.carrier_name} ${shippingMethod.shipping_name}`
}

export const validateIsPackagelessOrder = orderLines =>
  orderLines?.length === 1 && Number(orderLines[0].quantity) === 1 && orderLines[0]?.product?.is_packageless === true

export const validateHasPackagelessProduct = orderLines => (orderLines?.some(orderLine => orderLine?.product?.is_packageless === true) ? true : false)

// Printing-type values shared with the attachments table
// (src/pages/Orders/GenerateShipment/sections/AttachmentsListSection/constants.js
// re-exports this) so the magic strings live in a single place.
export const DOCUMENT_TYPES = {
  LABEL: 'label',
  DOCUMENT: 'document',
  IDENTIFIER_LABEL: 'identifier_label',
}

// tracking_number can arrive as a number or as a client-provided string with
// stray whitespace (the public API / manual-upload endpoints permit
// `metadata.tracking_number` verbatim), while a label's shipping_number is a
// backend value. Normalize both sides so the comparison matches on the digits
// only, not on type or padding.
const normalizeTrackingNumber = value => String(value).trim()

// Decides which shipping documents should actually be printed.
//
// Beyond the existing rules (skip already-printed and .png documents), a
// `label` document that is a superseded plugin guide is skipped — e.g. the
// original single-folio label that a multi-package (multi-OS) conversion
// replaced with several new folios. Printing it produces an extra, unwanted
// label.
//
// A label document is only skipped when there is POSITIVE evidence it was
// superseded, not merely because its tracking is absent from the VALID labels:
//   (a) its tracking matches a shipping label the order has that is no longer
//       VALID (INVALID/superseded) — it WAS a real label and got replaced; or
//   (b) the order has more than one VALID label — the multi-package (multi-OS)
//       conversion signature, where one plugin folio was split into several.
// An unmatched tracking on its own is NOT treated as superseded: a marketplace
// / external guide (or a manual-upload / public-API document) legitimately
// carries a tracking that never appears among the carrier's shipping labels and
// coexists with a single VALID carrier label — suppressing it would drop the
// only printable guide (BUGS-7363 regression). Suppression also stays off until
// the order's VALID labels are known (non-empty), preserving prior behavior when
// data hasn't loaded. A label matching a VALID label, or without a tracking
// number, is kept. Non-label documents are never affected.
//
// Every suppression is logged (id + tracking + the evidence that triggered it)
// so a wrongful drop can be told apart from a correct one when ops report a
// missing label.
export const filterPrintableShippingDocuments = (shippingDocuments = [], shippingLabels = []) => {
  const labels = shippingLabels || []
  const validLabelTrackingNumbers = new Set(
    labels.filter(label => label.status === 'VALID' && label.shipping_number).map(label => normalizeTrackingNumber(label.shipping_number)),
  )
  // Trackings that belong to a shipping label the order once had but that is
  // no longer VALID — positive evidence (a) that a matching document is stale.
  const supersededLabelTrackingNumbers = new Set(
    labels.filter(label => label.status !== 'VALID' && label.shipping_number).map(label => normalizeTrackingNumber(label.shipping_number)),
  )
  // Positive evidence (b): a multi-package (multi-OS) conversion produces more
  // than one VALID folio, and that is the only shape in which an unbacked label
  // document is a superseded plugin guide rather than a separate external one.
  const isMultiPackageConversion = validLabelTrackingNumbers.size > 1

  return (shippingDocuments || []).filter(shippingDocument => {
    if (shippingDocument.print) return false
    if (shippingDocument.file?.path?.endsWith('.png')) return false

    if (shippingDocument.printing_type === DOCUMENT_TYPES.LABEL && validLabelTrackingNumbers.size > 0) {
      const trackingNumber = shippingDocument.metadata?.tracking_number
      const normalized = trackingNumber ? normalizeTrackingNumber(trackingNumber) : null
      const backedByValidLabel = normalized !== null && validLabelTrackingNumbers.has(normalized)

      if (normalized !== null && !backedByValidLabel) {
        const matchedSupersededLabel = supersededLabelTrackingNumbers.has(normalized)
        if (matchedSupersededLabel || isMultiPackageConversion) {
          console.warn('[filterPrintableShippingDocuments] skipping label document superseded by newer VALID shipping label(s)', {
            documentId: shippingDocument.id,
            trackingNumber: normalized,
            validLabelTrackingNumbers: Array.from(validLabelTrackingNumbers),
            matchedSupersededLabel,
            multiPackageConversion: isMultiPackageConversion,
          })
          return false
        }
      }
    }

    return true
  })
}

export const validateIsFragileOrder = orderLines => {
  const isFragile = !orderLines
    ? false
    : orderLines?.some(orderLine => {
        // Guard once: all three reads below are on `product`.
        if (!orderLine.product) return false

        if (orderLine.product.is_fragile) {
          return true
        }

        if (orderLine.product.is_kit && orderLine.product.kit_childs.some(productKit => productKit.child_product.is_fragile)) {
          return true
        }

        return false
      })

  return isFragile
}

// LABEL_TYPE_PICKUP is written only by PackingListService, whose label_file is always the packing
// list rather than a carrier guia.
export const PACKING_LIST_LABEL_TYPE = 'Pickup'

// The generate response carries no label_type (ShippingLabelSerializer omits it), so the type comes
// from the order payload. An unresolvable id prints: a stray A4 sheet beats dropping a carrier guia.
export const isPackingListLabel = (label, allLabels = []) => {
  const labelType = label?.label_type ?? allLabels.find(candidate => candidate?.id === label?.id)?.label_type
  return labelType === PACKING_LIST_LABEL_TYPE
}

export const buildPrintSources = ({ primaryLabel, extraLabels = [], printableDocuments = [], allLabels = [], shippingType, packagesCount }) => {
  const sources = []

  if (primaryLabel?.id && primaryLabel?.label_file?.path && !isPackingListLabel(primaryLabel, allLabels)) {
    sources.push({ kind: 'label_file', id: primaryLabel.id })
  }

  extraLabels.filter(label => !label.print && !isPackingListLabel(label, allLabels)).forEach(label => sources.push({ kind: 'label_file', id: label.id }))

  if (shippingType === 'international' && primaryLabel?.id && primaryLabel?.billing_file?.path) {
    sources.push({ kind: 'billing_file', id: primaryLabel.id })
  }

  // Every printing_type, not an allowlist: a new type must default to printing, not to vanishing.
  printableDocuments.forEach(document => {
    const source = { kind: 'shipping_document', id: document.id }
    if (document.printing_type === DOCUMENT_TYPES.IDENTIFIER_LABEL && packagesCount > 1) source.quantity = packagesCount
    sources.push(source)
  })

  if (primaryLabel?.invoice_label_file?.path) sources.push({ kind: 'invoice_label' })

  // The cascade's fallback branch reads shipping_labels[0], not the generated label, with no
  // shipping_type check. The endpoint resolves label ids through .valid, so a non-VALID one would
  // only come back as an unexplained skipped count.
  if (!primaryLabel?.billing_file) {
    const fallback = allLabels[0]
    if (fallback?.id && fallback?.billing_file?.path && fallback?.status === 'VALID') {
      sources.push({ kind: 'billing_file', id: fallback.id })
    }
  }

  return sources
}

// Nothing has printed by the time any of these resolve, so every failure falls back to the
// per-document cascade rather than stranding the packer on an error with no paper.
export const resolveBundleOutcome = ({ sources = [], response, error }) => {
  if (!sources.length) return { action: 'cascade', reason: 'no_bundleable_sources' }
  if (error) return { action: 'cascade', reason: 'request_failed', status: error?.response?.status ?? null }
  if (!response?.bundles?.length) return { action: 'cascade', reason: 'empty_bundles', skipped: response?.skipped_count || 0 }

  return { action: 'preview', bundles: response.bundles, skipped: response.skipped_count || 0 }
}

// Mirrors platform Order::REMOVE_STOCK_ORDER_TYPES.
export const REMOVAL_ORDER_TYPES = ["REMOVE_STOCK", "REMOVE_DAMAGED_STOCK", "REMOVE_EXPIRED_STOCK", "REMOVE_LOST_STOCK"]
// Each removal type emits its own purpose (platform Order#removal_invoice_purpose), so a
// list that only knows REMOVE_STOCK_INVOICE hides the DANFE of damaged/expired/lost removals.
export const PRINTABLE_INVOICE_PURPOSES = [
  "SALE_INVOICE",
  "DONATION_INVOICE",
  ...REMOVAL_ORDER_TYPES.map(type => `${type}_INVOICE`),
]

export const findPrintableInvoiceIndex = (invoices = []) =>
  invoices.findIndex(invoice => PRINTABLE_INVOICE_PURPOSES.includes(invoice.purpose) && invoice.status === 'APPROVED')

// Above this piece count the packing deadline is agreed with the brand and
// set manually by a warehouse manager (mirrors the platform tier table cap).
export const REM_MANUAL_PACKING_TIME_THRESHOLD = 2000

export const needsManualRemPackingTime = (order) =>
  REMOVAL_ORDER_TYPES.includes(order.order_type) &&
  Number(order.assigned_stock_quantity) > REM_MANUAL_PACKING_TIME_THRESHOLD &&
  !order.max_packing_time
